import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, ButtonGroup, Container } from 'react-bootstrap';
import { Archive, Eye, ColumnsGap, Table, PencilFill, Person, Plus, X } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { ABORT_ERROR } from '../../helpers/api';
import { APARTMENT_STATUS_LABELS, REAL_ESTATE_ANALYST_ROLE } from '../../helpers/constants';
import {
  apartmentTypes,
  isAdmin,
  isCustomer,
  updateItemsInArray,
  formatValidationError,
  sanitizeFilters
} from '../../helpers/global';
import { apartmentTableColumns } from '../../helpers/overviewColumnsFields';
import useAuth from '../../hooks/useAuth';
import useDebouncedEffect from '../../hooks/useDebouncedEffect';
import useLocalization from '../../hooks/useLocalization';
import { apartmentServices } from '../../services/appartmentServices';
import { statusOrderServices } from '../../services/statusOrderServices';
import CustomerSelectModal from '../calculators/CustomerSelectModal';
import AlertModal from '../common/AlertModal';
import DataTable from '../common/data-table/DataTable';
import FloatingButton from '../common/FloatingButton';
import AddEditApartmentModal from './AddEditApartmentModal';
import GroupStatus from './GroupStatus';
import { useLocation } from 'react-router-dom';
import useSelection from '../../hooks/useSelection';
import UpdateMultipleAppartmentSidebar from './UpdateMultipleAppartmentSidebar';

const DEFAULT_PAGE_SIZE = 50;

const defaultTableSortOption = {
  key: 'createdAt',
  order: 'desc'
};
const initialPageInfo = {
  pageNumber: 1,
  totalPages: 1
};

// The statuses that trigger the archive alert before saving the apartment
const ARCHIVE_ALERT_TRIGGER_STATUSES = [
  APARTMENT_STATUS_LABELS.irrelevant,
  APARTMENT_STATUS_LABELS.bought_by_outsider,
  APARTMENT_STATUS_LABELS.bought_by_our_client
];
const getTableFilterValues = (fields, apartmentProfile) => {
  if (!apartmentProfile) return [];
  const filters = [];

  fields?.forEach(({ type, key, apartmentProfileKey }) => {
    if (apartmentProfile.hasOwnProperty(apartmentProfileKey || key) && (apartmentProfileKey || key) !== 'status') {
      const value = apartmentProfile[apartmentProfileKey || key];

      if (type === 'switch') {
        filters.push({ type, key, value: value === 'No' ? value : '' });
      }

      if (type === 'text') {
        filters.push({ type, key, value: value || '' });
      }
      if (type === 'number') {
        let from = value?.min;
        let to = value?.max;
        if (key === 'areaInSquareMeters') {
          from = value === 'No' ? 50 : null;
          to = null;
        }
        if (from || to) {
          filters.push({
            type,
            key,
            value: { from, to }
          });
        }
      }
    }
  });

  return filters;
};

const allStatusDefault = () =>
  Object.values(APARTMENT_STATUS_LABELS)
    .filter(Boolean)
    .sort();
const BrowseAppartments = ({ adminCompTable, assigneUsers }) => {
  const abortControllerRef = useRef(null);

  const { user } = useAuth();
  const { translate, isRTL } = useLocalization();

  const [tableSortOption, setTableSortOption] = useState(defaultTableSortOption);
  const [tableFilterValues, setTableFilterValues] = useState([]);

  const [fetchingFirstPageApartments, setFetchingFirstPageApartments] = useState(false);
  const [fetchingMoreApartments, setFetchingMoreApartments] = useState(false);
  //original apartment array
  const [apartments, setApartments] = useState([]);
  const [toLoadPageInfo, setToLoadPageInfo] = useState();
  const [loadedPageInfo, setLoadedPageInfo] = useState();

  const [addEditApratmentModalMeta, setAddEditApartmentModalMeta] = useState(null);
  const [deleteApartmentMeta, setDeleteApartmentMeta] = useState(null);
  const [duplicateApartmentMeta, setDuplicateApartmentMeta] = useState(null);
  const [creatingDriveFolder, setCreatingDriveFolder] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [apartmentProfile, setApartmentProfile] = useState(null);
  const [showCustomerSelectionModal, setShowCustomerSelectionModal] = useState(false);
  const [selectedApartment, setSelectedApartment] = useState(null);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const apartmentIdFromUrl = searchParams.get('apartmentId');
  const [showMultiEditSidebar, setShowMultiEditSidebar] = useState(false);
  const [showMoreStatuses, setShowMoreStatuses] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveStatusChangeMeta, setArchiveStatusChangeMeta] = useState(null);
  const [viewAsStatusGroup, setViewAsStatusGroup] = useState(false);
  // trigger action for the status table
  const [triggerActionForStatusTable, setTriggerActionForStatusTable] = useState({
    statuses: [],
    trigger: false
  });
  const statusOrderRef = useRef(allStatusDefault());

  const {
    selectAllMode,
    selectedItems,
    unselectedItems,
    isItemSelected,
    onToggleSelectItem,
    onToggleSelectAll
  } = useSelection(apartments);

  const onAddNewApartmentClick = () => {
    let realEstateAnalystId = null;

    if (user?.role === REAL_ESTATE_ANALYST_ROLE) {
      realEstateAnalystId = user._id;
    } else {
      // Find the first Real Estate Analyst user
      const realEstateAnalysts = assigneUsers?.filter(u => u.role === REAL_ESTATE_ANALYST_ROLE && !u.archived) || [];
      realEstateAnalystId = realEstateAnalysts[0]?._id || null;
    }

    setAddEditApartmentModalMeta({
      mode: 'add',
      data: {
        type: apartmentTypes[0]?.value,
        status: APARTMENT_STATUS_LABELS.gathering_info,
        realEstateAnalyst: realEstateAnalystId
      }
    });
  };

  const onEditApartmentClick = apartment => {
    setAddEditApartmentModalMeta({
      mode: 'update',
      data: apartment
    });
  };

  const onDeleteApartmentClick = apartmentTodelete => {
    setDeleteApartmentMeta({ apartmentTodelete });
  };
  const onArchiveApartmentStatusChangeSuccess = (apartmentId, status) => {
    setApartments(prev => prev.filter(a => a?._id !== apartmentId));
    if (viewAsStatusGroup) {
      setTriggerActionForStatusTable({
        status: [status],
        trigger: true
      });
    }
    setLoadedPageInfo({ ...loadedPageInfo, resultCount: loadedPageInfo.resultCount - 1 });
  };

  const onDuplicateApartmentClick = apartmentToDuplicate => {
    setDuplicateApartmentMeta({ apartmentToDuplicate });
  };

  const tableColumns = useMemo(
    () =>
      apartmentTableColumns({
        translate,
        users: assigneUsers,
        setSelectedApartment,
        selectedApartment,
        selectAllMode,
        onToggleSelectAll,
        onToggleSelectItem,
        isItemSelected,
        showMoreStatuses
      }),
    [
      translate,
      assigneUsers,
      selectedApartment,
      selectAllMode,
      onToggleSelectAll,
      onToggleSelectItem,
      isItemSelected,
      showMoreStatuses
    ]
  );

  const deleteApartment = async () => {
    try {
      let apartmentTodelete = deleteApartmentMeta?.apartmentTodelete;
      setDeleteApartmentMeta({ ...deleteApartmentMeta, showProgress: true });
      let { response, error } = await apartmentServices.deleteApartment(apartmentTodelete?._id);
      if (error) {
        setDeleteApartmentMeta({ ...deleteApartmentMeta, showProgress: false });
        return toast.error(error);
      } else {
        let newApartments = apartments?.filter(a => a._id !== apartmentTodelete?._id);
        setApartments(newApartments);

        // Trigger refresh for GroupStatus if in status group view
        if (viewAsStatusGroup) {
          setTriggerActionForStatusTable({
            status: [apartmentTodelete.status],
            trigger: true
          });
        }

        setDeleteApartmentMeta(null);
        setAddEditApartmentModalMeta(null);
        setLoadedPageInfo({ ...loadedPageInfo, resultCount: loadedPageInfo.resultCount - 1 });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const duplicateApartment = async () => {
    try {
      let apartmentToDuplicate = duplicateApartmentMeta?.apartmentToDuplicate;
      setDuplicateApartmentMeta({ ...duplicateApartmentMeta, showProgress: true });
      let { response, error } = await apartmentServices.duplicateApartment(apartmentToDuplicate?._id);
      if (error) {
        setDuplicateApartmentMeta({ ...duplicateApartmentMeta, showProgress: false });
        return toast.error(error);
      } else {
        toast.success('Apartment has been duplicated');
        let updatedApartments = updateItemsInArray(apartments, response);
        setApartments(updatedApartments);
        setDuplicateApartmentMeta(null);
        setLoadedPageInfo({ ...loadedPageInfo, resultCount: loadedPageInfo.resultCount + 1 });
      }
    } catch (error) {
      // Error handling is done by the service layer
    }
  };

  const fetchApartments = async () => {
    // Abort previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (toLoadPageInfo?.pageNumber === 1) {
      setFetchingFirstPageApartments(true);
    } else if (toLoadPageInfo?.pageNumber > 1) {
      setFetchingMoreApartments(true);
    }

    const controller = new AbortController();
    const { signal } = controller;
    abortControllerRef.current = controller;

    let sanitizedFilters = sanitizeFilters(tableFilterValues);

    const requestBody = {
      pageSize: toLoadPageInfo?.pageSize || DEFAULT_PAGE_SIZE,
      pageNumber: toLoadPageInfo?.pageNumber || 1,
      sort: tableSortOption,
      filter: sanitizedFilters,
      ignoreEmptyValuesWhileFiltering: Boolean(apartmentProfile)
    };

    const { response, error } = await apartmentServices.getApartments(requestBody, signal);
    if (error === ABORT_ERROR) return;
    if (error) {
      toast.error(error);
      return;
    }

    const { pageNumber: pgNumber = 1, totalPages = 1, resultCount = 0, results = [] } = response || {};

    if (pgNumber === 1) {
      setFetchingFirstPageApartments(false);
    } else if (pgNumber > 1) {
      setFetchingMoreApartments(false);
    }
    // Update page info
    setLoadedPageInfo({
      pageNumber: pgNumber,
      totalPages: totalPages,
      resultCount: resultCount
    });

    if (pgNumber === 1) {
      setApartments(results);
    } else {
      setApartments(prevApartments => [...prevApartments, ...results]);
    }
  };

  const loadMoreData = () => {
    if (!loadedPageInfo || fetchingFirstPageApartments || fetchingMoreApartments) return;

    if (loadedPageInfo.totalPages < loadedPageInfo.pageNumber) return;

    setToLoadPageInfo({
      ...loadedPageInfo,
      pageNumber: loadedPageInfo.pageNumber + 1,
      scrollHeight: undefined
    });
  };

  const createDriveFolderInApartment = async apartmentID => {
    try {
      setCreatingDriveFolder(true);
      let { response, error } = await apartmentServices.createDriveFolder({
        _id: apartmentID
      });
      if (error) {
        return toast.error(error);
      }
      if (response) {
        toast.success('Drive folder has been created');
        let updatedApartments = updateItemsInArray(apartments, response);
        setApartments(updatedApartments);
        if (Boolean(addEditApratmentModalMeta)) {
          setAddEditApartmentModalMeta({ ...addEditApratmentModalMeta, data: response });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setCreatingDriveFolder(false);
    }
  };

  const handleAddUpdateApartment = async (app, createNewFolderOnly = false, saveAndClose = true) => {
    try {
      // validate check for city and real estate analyst fields
      if (!app?.city || app.city.trim() === '') {
        toast.warning('City field is required. Please select a city.');
        return;
      }

      if (!app?.realEstateAnalyst) {
        toast.warning('Real Estate Analyst field is required. Please select an analyst.');
        return;
      }

      const isEditMode = addEditApratmentModalMeta?.mode === 'update';
      setAddEditApartmentModalMeta({
        ...addEditApratmentModalMeta,
        ...(createNewFolderOnly
          ? { creatingNewFolder: true }
          : { showProgress: saveAndClose, isSavingOnly: !saveAndClose })
      });

      const apartmentData = {
        ...app,
        realEstateAnalyst: app?.realEstateAnalyst || null
      };

      let { response, error } = isEditMode
        ? await apartmentServices.updateApartment(app?._id, apartmentData)
        : await apartmentServices.createApartment(apartmentData);
      if (error) {
        setAddEditApartmentModalMeta({
          ...addEditApratmentModalMeta,
          showProgress: false,
          creatingNewFolder: false,
          isSavingOnly: false
        });
        const formattedError = formatValidationError(error);
        return toast.error(formattedError, {
          style: { whiteSpace: 'pre-line' }
        });
      }
      if (response) {
        toast.success(
          createNewFolderOnly
            ? 'New folder has been created'
            : isEditMode
            ? 'Apartment has been updated'
            : 'New apartment has been added'
        );

        let updatedApartments = updateItemsInArray(apartments, response);
        setApartments(updatedApartments);
        if (!isEditMode || createNewFolderOnly) {
          setLoadedPageInfo({ ...loadedPageInfo, resultCount: loadedPageInfo.resultCount + 1 });
        }

        // Trigger refresh for GroupStatus if in status group view
        if (viewAsStatusGroup) {
          setTriggerActionForStatusTable({
            status: [response.status],
            trigger: true
          });
        }

        if (createNewFolderOnly) {
          setAddEditApartmentModalMeta({
            ...addEditApratmentModalMeta,
            mode: 'update',
            showProgress: false,
            creatingNewFolder: false,
            data: response
          });
        } else {
          if (saveAndClose) {
            setAddEditApartmentModalMeta(null);
          } else {
            setAddEditApartmentModalMeta({
              ...addEditApratmentModalMeta,
              isSavingOnly: false,
              mode: 'update',
              data: response
            });
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const onUpdateMultipleApartments = async ({
    apartmentUpdates = {},
    fieldsToBeAdded = [],
    fieldsToBeRemoved = [],
    options = {}
  }) => {
    if (!selectedItems.length && !selectAllMode) {
      toast.error('Please select at least one apartment');
      return;
    }

    const requestBody = {
      apartmentUpdates,
      fieldsToBeAdded,
      fieldsToBeRemoved,
      selectedApartments: selectedItems.map(a => a._id),
      unselectedApartments: unselectedItems.map(a => a._id),
      selectAllMode: selectAllMode,
      filter: tableFilterValues,
      sort: tableSortOption
    };

    const { response, error } = await apartmentServices.updateMultipleApartments(requestBody);
    if (error) {
      return toast.error(error);
    }

    if (response) {
      if (response.failedUpdates.length === 0) {
        toast.success('All apartments have been updated');
      } else {
        toast.warning(`${response.failedUpdates.length} apartments could not be updated. Please check for errors.`);
      }

      await fetchApartments();
    }
  };

  const handleUpdateApartment = async (overrides = {}) => {
    try {
      const payload = { ...selectedApartment, ...overrides, showProgress: true };

      setSelectedApartment(payload);
      let { response, error } = await apartmentServices.updateApartment(selectedApartment?._id, payload);
      if (error) {
        setSelectedApartment(null);
        return toast.error(error);
      }
      if (response) {
        toast.success('Apartment has been updated');
        setApartments(updateItemsInArray(apartments, response));
        // Trigger refresh for GroupStatus if in status group view
        if (viewAsStatusGroup) {
          // get the old apartment status from selectedApartment
          const oldApartmentStatus = selectedApartment?.oldStatus;
          const newApartmentStatus = response?.status;
          const hasStatusChanged = oldApartmentStatus !== newApartmentStatus;
          setTriggerActionForStatusTable({
            status: hasStatusChanged ? [newApartmentStatus, oldApartmentStatus] : [oldApartmentStatus],
            trigger: true
          });
        }

        // filter out archived updated apartment based on current archived state (showArchived)
        if (response.isApartmentArchived !== showArchived) {
          // delay the filtering
          setTimeout(() => {
            onArchiveApartmentStatusChangeSuccess(response?._id, response.status);
          }, 500);
        }
        setSelectedApartment(null);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const onConfirmArchiveOnStatusChange = currentApartmentArchivedStatus => {
    setArchiveStatusChangeMeta(null);
    // handle update apartment with overrides to archive the apartment
    handleUpdateApartment({ isApartmentArchived: !currentApartmentArchivedStatus });
  };

  const onDismissArchiveOnStatusChange = () => {
    setArchiveStatusChangeMeta(null);
    // handle update apartment with overrides to dismiss the archive status change
    handleUpdateApartment();
  };

  const loadSingleApartment = async () => {
    const apartmentId = apartmentIdFromUrl || sessionStorage.getItem('apartmentId');
    if (apartmentId) {
      try {
        const requestBody = {
          pageSize: 1,
          pageNumber: 1,
          filter: [{ key: '_id', value: apartmentId }]
        };
        console.log('loadSingleApartment requestBody');
        const { response, error } = await apartmentServices.getApartments(requestBody);
        if (!error && response) {
          setAddEditApartmentModalMeta({
            mode: 'update',
            data: response?.results[0]
          });
        }
      } catch (err) {
        console.error('Failed to fetch apartment:', err);
        toast.error('Could not load apartment details');
      }

      sessionStorage.removeItem('apartmentId');
    }
  };

  const handleOnFilterValuesChange = values => {
    const filteredValues = values.filter(
      v => v.key !== 'status' || (!v.value.includes('Show more...') && !v.value.includes('Show less...'))
    );

    if (values.find(v => v.key === 'status' && v.value.includes('Show more...'))) {
      setShowMoreStatuses(true);
    } else if (values.find(v => v.key === 'status' && v.value.includes('Show less...'))) {
      setShowMoreStatuses(false);
    } else {
      setTableFilterValues(filteredValues);
    }
  };

  useEffect(() => {
    fetchApartments();
  }, [toLoadPageInfo]);

  useEffect(() => {
    let updatedTableFilterValues = getTableFilterValues(tableColumns, apartmentProfile);
    const filtered = updatedTableFilterValues.filter(f => f?.key !== 'isApartmentArchived');
    setTableFilterValues([...filtered, { key: 'isApartmentArchived', value: showArchived }]);
  }, [apartmentProfile, showArchived]);

  useDebouncedEffect(
    () => {
      // Reset to first page when filters change and clear previous results to prevent duplicates
      setApartments([]);
      setLoadedPageInfo(undefined);
      setToLoadPageInfo({ ...initialPageInfo, scrollHeight: 0 });
    },
    [tableFilterValues, tableSortOption, showArchived],
    500
  );

  useEffect(() => {
    if (selectedApartment?.updateKey === 'status' && !selectedApartment?.showProgress) {
      const currentApartmentArchivedStatus = selectedApartment.currentApartmentArchivedStatus;
      if (!currentApartmentArchivedStatus && ARCHIVE_ALERT_TRIGGER_STATUSES.includes(selectedApartment.status)) {
        setArchiveStatusChangeMeta({ show: true, currentApartmentArchivedStatus });
        return;
      } else if (currentApartmentArchivedStatus && !ARCHIVE_ALERT_TRIGGER_STATUSES.includes(selectedApartment.status)) {
        setArchiveStatusChangeMeta({ show: true, currentApartmentArchivedStatus });
        return;
      }
      handleUpdateApartment();
    }
  }, [selectedApartment?.updateKey, selectedApartment?.showProgress]);

  const handleApartmentAfterReportCreateOrUpdate = (apartmentID, changes) => {
    let updatedApartments = apartments?.map(a => {
      if (a?._id === apartmentID) {
        return { ...a, ...changes };
      }
      return a;
    });
    setApartments(updatedApartments);
  };

  const handleApartmentAfterNoteUpdate = (apartmentID, notes) => {
    let updatedApartments = apartments?.map(a => {
      if (a?._id === apartmentID) {
        return { ...a, notes };
      }
      return a;
    });
    setApartments(updatedApartments);
  };

  useEffect(() => {
    loadSingleApartment();
  }, []);

  // Fetch status order on mount
  useEffect(() => {
    const fetchStatusOrder = async () => {
      const { response, error } = await statusOrderServices.getStatusOrder();
      if (error) {
        toast.error(error);
        return;
      }
      if (response && response.statusOrder.length > 0) {
        statusOrderRef.current = response.statusOrder;
      }
    };

    fetchStatusOrder();
  }, []);

  return (
    <>
      <Container fluid className={'px-2'}>
        <div className="pt-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center gap-2 py-2">
              {isCustomer(user?.role) && (
                <h6 className="mb-0">
                  {translate('apartments')} ({loadedPageInfo?.resultCount ?? 0})
                </h6>
              )}
              {isAdmin(user?.role) && (
                <>
                  {!viewAsStatusGroup && (
                    <>
                      <Button className="d-flex justify-content-between align-items-center" variant="dark" size="sm">
                        <span className="text-white mx-2 smallFont">
                          {selectAllMode
                            ? loadedPageInfo?.resultCount - unselectedItems?.length
                            : selectedItems?.length}{' '}
                          /{loadedPageInfo?.resultCount ?? 0} {translate('apartments')}
                        </span>
                      </Button>
                      <Button
                        variant={showMultiEditSidebar ? 'dark text-white' : 'outline-dark'}
                        onClick={() => setShowMultiEditSidebar(!showMultiEditSidebar)}
                        className="px-2 m-0 py-1 d-flex align-items-center gap-2"
                        size="sm"
                      >
                        <PencilFill size={15} />
                      </Button>
                    </>
                  )}
                  <Button
                    variant={showArchived ? 'dark' : 'outline-dark'}
                    onClick={() => setShowArchived(prev => !prev)}
                    className="px-2 m-0 py-1 d-flex align-items-center gap-2"
                    size="sm"
                  >
                    <Archive size={15} />
                  </Button>
                </>
              )}
            </div>
            {isAdmin(user?.role) && (
              <div
                className="d-flex align-items-center justify-content-center flex-grow-1 gap-2 mx-3"
                style={{ overflow: 'visible' }}
              >
                <Button
                  variant={!viewAsStatusGroup ? 'dark' : 'outline-dark'}
                  onClick={() => setViewAsStatusGroup(false)}
                  className="d-flex align-items-center gap-2 px-2"
                  size="sm"
                >
                  <Table size={14} />
                  <span className="smallFont">{translate('table_view')}</span>
                </Button>
                <Button
                  variant={viewAsStatusGroup ? 'dark' : 'outline-dark'}
                  onClick={() => setViewAsStatusGroup(true)}
                  className="d-flex align-items-center gap-2 px-2"
                  size="sm"
                >
                  <ColumnsGap size={14} />
                  <span className="smallFont">{translate('group_view')}</span>
                </Button>
              </div>
            )}
            <div className="d-flex align-items-center gap-1">
              <h6 className="mid mb-0 fw-bold">{translate('show_apartments_for')}:</h6>
              <ButtonGroup className="m-0">
                <Button
                  className="d-inline-flex align-items-center  fw-bold mx-0"
                  variant="outline-dark"
                  size="sm"
                  onClick={() => setShowCustomerSelectionModal(true)}
                  style={{ fontSize: 12 }}
                >
                  <Person className="mx-1" />
                  {selectedCustomer ? selectedCustomer.name : translate('select_customer')}
                </Button>{' '}
                {selectedCustomer &&
                  selectedCustomer?.apartmentProfiles?.length > 0 &&
                  selectedCustomer?.apartmentProfiles?.map((a, i) => {
                    return (
                      <Button
                        className="d-flex align-items-center mx-0"
                        variant={apartmentProfile?._id === a?._id ? 'dark' : 'outline-dark'}
                        size="sm"
                        onClick={() => setApartmentProfile(a)}
                      >
                        P{i + 1}
                      </Button>
                    );
                  })}
                {selectedCustomer && (
                  <Button
                    className="d-flex align-items-center  fw-bold"
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      setApartmentProfile(null);
                      setSelectedCustomer(null);
                    }}
                  >
                    <X size={16} />
                  </Button>
                )}
              </ButtonGroup>
            </div>
          </div>
          <div className="d-flex align-items-start gap-2 position-relative">
            {showMultiEditSidebar && !viewAsStatusGroup && (
              <UpdateMultipleAppartmentSidebar
                assigneUsers={assigneUsers}
                setShowMultiEditSidebar={setShowMultiEditSidebar}
                onFieldValueSave={onUpdateMultipleApartments}
              />
            )}
            <div
              style={{
                width: showMultiEditSidebar && !viewAsStatusGroup ? '79.5%' : '100%',
                marginLeft: showMultiEditSidebar && !viewAsStatusGroup && !isRTL ? '20.5%' : '0',
                marginRight: showMultiEditSidebar && !viewAsStatusGroup && isRTL ? '20.5%' : '0',
                transition: 'all 0.3s ease-in-out'
              }}
            >
              {viewAsStatusGroup ? (
                <GroupStatus
                  rowKey="_id"
                  apartmentProfile={apartmentProfile}
                  showArchived={showArchived}
                  getTableFilterValues={getTableFilterValues}
                  onRowClick={onEditApartmentClick}
                  users={assigneUsers}
                  setSelectedApartment={setSelectedApartment}
                  selectedApartment={selectedApartment}
                  showMoreStatuses={showMoreStatuses}
                  statusTableTriggerAction={triggerActionForStatusTable}
                  setStatusTableTriggerAction={setTriggerActionForStatusTable}
                  statusOrderRef={statusOrderRef}
                />
              ) : (
                <DataTable
                  maxTableHeight={`calc(100vh - 116px)`}
                  rowKey={'_id'}
                  columns={tableColumns}
                  data={apartments}
                  bottomOffset={300}
                  onBottomReached={loadMoreData}
                  onRowClick={onEditApartmentClick}
                  loadingMoreData={fetchingMoreApartments}
                  loadingFirstPageData={fetchingFirstPageApartments}
                  sortOptions={tableSortOption}
                  onSortChange={setTableSortOption}
                  filterValues={tableFilterValues}
                  onFilterValuesChange={handleOnFilterValuesChange}
                />
              )}
            </div>
          </div>
        </div>
        {Boolean(addEditApratmentModalMeta) && (
          <AddEditApartmentModal
            show={Boolean(addEditApratmentModalMeta)}
            onHide={() => setAddEditApartmentModalMeta(null)}
            initialData={addEditApratmentModalMeta?.data}
            adminCompTable={adminCompTable}
            editMode={addEditApratmentModalMeta?.mode === 'update'}
            onSubmit={handleAddUpdateApartment}
            onDeleteApartmentClick={onDeleteApartmentClick}
            showProgress={addEditApratmentModalMeta?.showProgress}
            isSavingOnly={addEditApratmentModalMeta?.isSavingOnly}
            creatingNewFolder={addEditApratmentModalMeta?.creatingNewFolder}
            createDriveFolderInApartment={createDriveFolderInApartment}
            creatingDriveFolder={creatingDriveFolder}
            assigneUsers={assigneUsers}
            onArchiveApartmentStatusChangeSuccess={onArchiveApartmentStatusChangeSuccess}
            handleApartmentAfterReportCreateOrUpdate={handleApartmentAfterReportCreateOrUpdate}
            handleApartmentAfterNoteUpdate={handleApartmentAfterNoteUpdate}
            onDuplicateApartmentClick={onDuplicateApartmentClick}
          />
        )}
        <CustomerSelectModal
          show={showCustomerSelectionModal}
          onHide={() => setShowCustomerSelectionModal(false)}
          selectedCustomer={selectedCustomer}
          onCustomerSelect={cust => {
            if (cust?.apartmentProfiles?.length > 0) {
              setSelectedCustomer(cust);
              setApartmentProfile(cust?.apartmentProfiles[0]);
              setShowCustomerSelectionModal(false);
            } else {
              toast.info('This customer does not have any profile associated');
            }
          }}
          showSubmitButton={false}
          headerText="Select customer"
        />

        <AlertModal
          show={Boolean(deleteApartmentMeta)}
          onHide={() => setDeleteApartmentMeta(null)}
          onDismissClick={() => setDeleteApartmentMeta(null)}
          onContinueClick={deleteApartment}
          showProgress={deleteApartmentMeta?.showProgress}
          alertText={'Are you sure you want to delete this apartment?'}
        />

        <AlertModal
          show={Boolean(selectedApartment?.updateKey === 'dates')}
          onHide={() => setSelectedApartment(null)}
          onDismissClick={() => setSelectedApartment(null)}
          onContinueClick={() => handleUpdateApartment()}
          showProgress={selectedApartment?.showProgress}
          alertText={translate('are_you_sure_you_want_to_update_this_apartment')}
        />
        <AlertModal
          show={Boolean(duplicateApartmentMeta)}
          onHide={() => setDuplicateApartmentMeta(null)}
          onDismissClick={() => setDuplicateApartmentMeta(null)}
          onContinueClick={duplicateApartment}
          showProgress={duplicateApartmentMeta?.showProgress}
          alertText={translate('duplicate_apartment_alert')}
        />
        <AlertModal
          show={Boolean(archiveStatusChangeMeta?.show)}
          onHide={onDismissArchiveOnStatusChange}
          onDismissClick={onDismissArchiveOnStatusChange}
          onContinueClick={() => onConfirmArchiveOnStatusChange(archiveStatusChangeMeta.currentApartmentArchivedStatus)}
          alertText={
            archiveStatusChangeMeta?.currentApartmentArchivedStatus
              ? translate('do_you_want_to_unarchive_the_apartment')
              : translate('do_you_want_to_archive_the_apartment')
          }
          continue2ButtonText={translate('no')}
          continue2ButtonVariant="danger"
          onContinue2Click={onDismissArchiveOnStatusChange}
        />
        <FloatingButton
          variant="success"
          className="text-white"
          Icon={Plus}
          onClick={onAddNewApartmentClick}
          text={translate('new_apartment')}
        />
      </Container>
    </>
  );
};

export default BrowseAppartments;
